import { useMemo, useState } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { Identity, FullIdentity } from '../types/identity';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { identityService } from '../services/identityService';

const useIdentity = (identityId: Identity['id']) => {
  const handleError = useErrorHandler();
  const clients = useSelector((state: RootState) => state.clients);

  const [identity, setIdentity] = useState<FullIdentity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Main client of the most recently logged-in brand, falling back to the registration brand.
  const latestBrandClient = useMemo(() => {
    if (!identity) return null;
    const brandCodes = [...(identity.logins ?? [])]
      .sort((a, b) => Date.parse(b.lastLogin) - Date.parse(a.lastLogin))
      .map((login) => login.brandId)
      .concat(identity.registeredAt);
    for (const brandCode of brandCodes) {
      const mainClient = clients.find(
        (client) => client.isMainClient && client.brandCode === brandCode
      );
      if (mainClient) return mainClient;
    }
    return null;
  }, [identity, clients]);

  const getIdentity = async () => {
    const getIdentityRequest = async () => {
      try {
        const identity = await identityService.getIdentity(identityId);
        setIdentity(identity);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch identity'));
        setIdentity(null);
      }
    };
    await handleRequest(getIdentityRequest);
  };

  const patchIdentity = async (identity: Partial<Identity> | null) => {
    const patchIdentityRequest = async () => {
      if (identity) {
        await identityService.patchIdentity(identityId, identity).then(
          () => getIdentity(),
          (e) => handleError(e)
        );
      }
    };
    await handleRequest(patchIdentityRequest);
  };

  const deleteIdentity = async () => {
    const deleteIdentityRequest = async () =>
      await identityService.deleteIdentity(identityId).then(() => setIdentity(null));
    await handleRequest(deleteIdentityRequest);
  };

  const invalidateAccessTokens = async (
    successHook?: () => void,
    errorHook?: (e?: Error) => void
  ) => {
    await identityService
      .invalidateAccessTokensForIdentity(identityId)
      .then(successHook, errorHook);
  };

  const handleRequest = async (customFunction: () => Promise<unknown>) => {
    setLoading(true);
    await customFunction().finally(() => setLoading(false));
  };

  return {
    identity,
    getIdentity,
    patchIdentity,
    deleteIdentity,
    invalidateAccessTokens,
    latestBrandClientForIdentity: latestBrandClient,
    identityLoading: loading,
    identityError: error,
  };
};

export type IdentityHook = typeof useIdentity;
export type IdentityHookResult = ReturnType<IdentityHook>;
export default useIdentity;
